/**
 * Correos del ciclo de vida del Free (freemium, 29-sep-2026).
 *
 *   · Bienvenida al registrarse gratis (notify kind 'welcome', 1 por usuario).
 *   · Verificación suave del correo: fila propia en email_verifications
 *     (Google cuenta como verificado). Reenvío con notify 'email_verification'.
 *   · Links de correo sin sesión: edge function email-link (confirmar / baja).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useUser } from '@/lib/auth';

/** Dispara la bienvenida del propio usuario. Fire-and-forget: nunca bloquea. */
export async function sendMyWelcome(userId: string): Promise<void> {
  try {
    await supabase.functions.invoke('notify', {
      body: { kind: 'welcome', payload: { user_id: userId } },
    });
  } catch {
    // Si falla, cron-free-nurture la manda de respaldo a los 20 minutos.
  }
}

/** ¿El correo del usuario actual está confirmado? */
export function useEmailVerified() {
  const user = useUser();
  const meta = (user?.app_metadata ?? {}) as { provider?: string; providers?: string[] };
  const viaGoogle = meta.provider === 'google' || (meta.providers ?? []).includes('google');
  return useQuery({
    queryKey: ['email-verified', user?.id],
    enabled: !!user,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<boolean> => {
      if (!user) return false;
      if (viaGoogle) return true;
      const { data, error } = await supabase
        .from('email_verifications')
        .select('user_id')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) return true; // ante la duda, no molestamos con el aviso
      return !!data;
    },
  });
}

/** Reenvía "Confirma tu correo". El servidor limita a 1 cada 5 min y 5 al día. */
export function useResendVerification() {
  const user = useUser();
  return useMutation({
    mutationFn: async (): Promise<'sent' | 'too_soon' | 'already' | 'limit'> => {
      if (!user) throw new Error('Sin sesión');
      const { data, error } = await supabase.functions.invoke('notify', {
        body: { kind: 'email_verification', payload: { user_id: user.id } },
      });
      if (error) throw new Error('No se pudo enviar el correo. Intenta en unos minutos.');
      const result = (data?.data ?? {}) as { skipped?: boolean; reason?: string };
      if (result.reason === 'already_verified') return 'already';
      if (result.reason === 'too_soon') return 'too_soon';
      if (result.reason === 'daily_limit') return 'limit';
      return 'sent';
    },
  });
}

type LinkResult = { ok: boolean; already?: boolean; reason?: 'invalid' | 'used' | 'expired' };

async function callEmailLink(action: 'verify' | 'unsubscribe', token: string): Promise<LinkResult> {
  const { data, error } = await supabase.functions.invoke('email-link', {
    body: { action, token },
  });
  if (error) throw new Error('No pudimos procesar el enlace. Intenta de nuevo en unos minutos.');
  return (data?.data ?? { ok: false, reason: 'invalid' }) as LinkResult;
}

export function useConfirmEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => callEmailLink('verify', token),
    onSuccess: (r) => {
      if (r.ok) qc.invalidateQueries({ queryKey: ['email-verified'] });
    },
  });
}

export function useUnsubscribeMarketing() {
  return useMutation({
    mutationFn: (token: string) => callEmailLink('unsubscribe', token),
  });
}
